// BlueprintImportContext — App-level, SELF-CONTAINED source of truth for an in-progress or
// just-completed "Upload My Template" import, PER PROJECT.
//
// WHY THIS EXISTS (mirrors BoardActivityContext.jsx's fix for the exact same class of bug):
//   ImportWizard used to own its upload/extract request AND its result in local component
//   state. That state lived only as long as the modal was mounted — but the modal is rendered
//   inside AutoDeck, which is rendered inside whichever page is currently active (Analyze or
//   Dashboard). App.jsx renders one page at a time, so clicking ANY main-nav item (Dashboard,
//   File Upload, Manage projects, …) unmounts the modal immediately. Reported live, twice:
//   extraction (confirmed 20-30+ seconds for a large multi-chart file) finished successfully on
//   the backend, but the customer had already navigated away, so the completed result — which
//   is never persisted anywhere until Publish — was silently thrown away with no way back.
//
//   A "don't close on backdrop click" guard (tried first) only covers clicking the dark overlay;
//   it can't survive a real navigation, because navigating doesn't ask the modal's permission —
//   it just unmounts the whole page. The only fix that actually survives navigation is the one
//   BoardActivityContext already proved out: move the state OUT of the page-scoped component and
//   into a provider mounted once, above the pages, in App.jsx. This context does that for
//   imports — the upload/extract request keeps running (or its finished result keeps existing)
//   no matter what the user clicks, and TemplateBanner (AutoDeck.jsx) reads it to show a
//   persistent "import running" / "import ready" indicator so returning to Analyze is never
//   "blind on what happens" (the exact complaint that prompted this fix).
import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { getFreshToken } from "../api";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
const MAX_BYTES = 50 * 1024 * 1024;   // mirrors backend/app/routers/blueprint.py's _MAX_IMPORT_BYTES

const BlueprintImportContext = createContext(null);

export function BlueprintImportProvider({ children }) {
  // { [projectId]: { status: "uploading"|"ready"|"error", fileName, result, error } }
  const [imports, setImports] = useState({});
  const mounted = useRef(true);

  const _patch = useCallback((projectId, patch) => {
    if (!mounted.current) return;
    setImports((prev) => ({ ...prev, [projectId]: { ...(prev[projectId] || {}), ...patch } }));
  }, []);

  const pollTimers = useRef({});   // { [projectId]: timeout id }
  const POLL_MS = 3000;

  // Polls GET /blueprint/import/status/{job_id} until the background job (see blueprint.py's
  // /import/start — extraction runs via an async self-invoke, same pattern as upload.py's
  // restore-job, roadmap #86) reaches a terminal state. Needed because the real extract+map+
  // preview/reconcile work took 19-33s against an actual multi-chart file — past API Gateway's
  // non-adjustable 30s integration timeout, so the ORIGINAL synchronous request got killed with
  // a 503 by the gateway even though the Lambda itself always finished successfully. Polling a
  // job id sidesteps that ceiling entirely: no single HTTP request needs to stay open longer
  // than a few seconds.
  const _pollStatus = useCallback(async (projectId, jobId) => {
    pollTimers.current[projectId] = null;
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_URL}/blueprint/import/status/${jobId}`, {
        headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "ready") {
          _patch(projectId, { status: "ready", result: data.result, error: null });
          return;
        }
        if (data.status === "error") {
          _patch(projectId, { status: "error", error: data.message || "Import failed. Please try again.", result: null });
          return;
        }
        // "processing" (or "unknown" briefly, right after kickoff before the DynamoDB write is
        // visible) — keep polling.
      }
      // Non-OK (transient/gateway) — keep polling rather than fail on one hiccup.
    } catch {
      // Network blip / token refresh hiccup — keep polling.
    }
    pollTimers.current[projectId] = setTimeout(() => _pollStatus(projectId, jobId), POLL_MS);
  }, [_patch]);

  // Kicks off the presign -> S3 PUT -> /blueprint/import/start flow and keeps running/polling to
  // completion regardless of which component called it or whether that component is still
  // mounted when it finishes — the SAME survives-unmount guarantee BoardActivityContext's poll
  // loop has.
  const startImport = useCallback((projectId, file) => {
    if (!projectId || !file) return;
    if (pollTimers.current[projectId]) { clearTimeout(pollTimers.current[projectId]); pollTimers.current[projectId] = null; }
    _patch(projectId, { status: "uploading", fileName: file.name, error: null, result: null });

    (async () => {
      try {
        if (file.size > MAX_BYTES) throw new Error(`File exceeds the ${MAX_BYTES / (1024 * 1024)}MB limit.`);
        const token = await getFreshToken();

        const presignRes = await fetch(`${API_URL}/blueprint/import/presign`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId, "Content-Type": "application/json" },
          body: JSON.stringify({ file_name: file.name, file_size: file.size }),
        });
        const presignData = await presignRes.json().catch(() => ({}));
        if (!presignRes.ok) throw new Error(presignData.detail || `Couldn't start the upload (${presignRes.status}).`);
        if (presignData.enabled === false) throw new Error("This feature isn't enabled for your account.");

        const putRes = await fetch(presignData.upload_url, {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          body: file,
        });
        if (!putRes.ok) throw new Error("Couldn't upload the file. Please try again.");

        const startRes = await fetch(`${API_URL}/blueprint/import/start`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId, "Content-Type": "application/json" },
          // prepare_for_tables: true on EVERY import (blueprint-tables-and-concepts Task 20a/21)
          // — a cheap server-side S3-to-S3 copy (never a second browser upload), always
          // self-cleaning (explicit discard on Tables-step completion, backstopped by a 1-day TTL
          // sweep either way), so there's no customer-facing cost to always requesting it. The
          // alternative (asking "also import summary tables?" before every upload) would add a
          // click to every import just to avoid a copy nobody will ever notice.
          body: JSON.stringify({ file_name: file.name, s3_key: presignData.s3_key, prepare_for_tables: true }),
        });
        const startData = await startRes.json().catch(() => ({}));
        if (!startRes.ok) throw new Error(startData.detail || `Couldn't start processing (${startRes.status}).`);
        if (startData.enabled === false) throw new Error("This feature isn't enabled for your account.");

        _patch(projectId, { status: "uploading" });   // still "uploading" in UI terms — extraction is now running
        pollTimers.current[projectId] = setTimeout(() => _pollStatus(projectId, startData.job_id), POLL_MS);
      } catch (e) {
        _patch(projectId, { status: "error", error: e.message || "Couldn't read this file.", result: null });
      }
    })();
  }, [_patch, _pollStatus]);

  // Called once the wizard has consumed the ready result (published, or the user explicitly
  // discarded it) — clears this project's slot so the banner/indicator goes away.
  const clearImport = useCallback((projectId) => {
    if (!projectId) return;
    if (pollTimers.current[projectId]) { clearTimeout(pollTimers.current[projectId]); pollTimers.current[projectId] = null; }
    setImports((prev) => {
      if (!(projectId in prev)) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }, []);

  const getImport = useCallback((projectId) => imports[projectId] || null, [imports]);

  useEffect(() => () => {
    mounted.current = false;
    Object.values(pollTimers.current).forEach((t) => t && clearTimeout(t));
  }, []);

  return (
    <BlueprintImportContext.Provider value={{ imports, startImport, clearImport, getImport }}>
      {children}
    </BlueprintImportContext.Provider>
  );
}

export function useBlueprintImport() {
  return useContext(BlueprintImportContext) || {
    imports: {}, startImport: () => {}, clearImport: () => {}, getImport: () => null,
  };
}
